import React from 'react';

const DEFAULT_INCOME_PAYMENT_LABEL = 'Income payments made by top 10,000 private corporations to their local/resident suppliers of services - Corporate';

function toNumber(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value, { blankWhenZero = false } = {}) {
  const amount = toNumber(value);
  if (blankWhenZero && amount === 0) {
    return '';
  }
  return amount.toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function resolveMonthSlots(monthOnly) {
  const month = Number(monthOnly || 0);
  return {
    month1: [1, 4, 7, 10].includes(month),
    month2: [2, 5, 8, 11].includes(month),
  };
}

function renderDetailRows(rows, fallbackMonthOnly) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const paddedRows = [...safeRows];
  while (paddedRows.length < 13) {
    paddedRows.push(null);
  }

  return paddedRows.map((row, index) => {
    if (!row) {
      return (
        <tr key={`fd2307-empty-row-${index}`}>
          <td />
          <td />
          <td />
          <td />
          <td />
          <td />
          <td />
        </tr>
      );
    }

    const transactionAmount = toNumber(row.transaction_amount);
    const withholdingTax = toNumber(row.withholding_tax);
    const atc = String(row.atc || 'WI150');
    const { month1, month2 } = resolveMonthSlots(row.month_only || fallbackMonthOnly);

    return (
      <tr key={`fd2307-row-${index}`}>
        <td>{row.income_label || DEFAULT_INCOME_PAYMENT_LABEL}</td>
        <td>{atc}</td>
        <td className="fd2307-text-right">{month1 ? formatNumber(transactionAmount) : ''}</td>
        <td className="fd2307-text-right">{month2 ? formatNumber(transactionAmount) : ''}</td>
        <td className="fd2307-text-right">{(!month1 && !month2) ? formatNumber(transactionAmount) : ''}</td>
        <td className="fd2307-text-right">{formatNumber(transactionAmount)}</td>
        <td className="fd2307-text-right">{formatNumber(withholdingTax)}</td>
      </tr>
    );
  });
}

export default function Form2307Sheet({ data, rootId }) {
  if (!data) {
    return null;
  }

  return (
    <div className="fd2307-page" id={rootId}>
      <div className="fd2307-tiny-top-meta">
        <div className="fd2307-meta-box">For BIR<br />Use Only</div>
        <div className="fd2307-meta-box">BCS/<br />Item:</div>
      </div>

      <div className="fd2307-title-top-box">
        <div className="fd2307-title-top">
          <img className="fd2307-seal-img" src="/birform2307logo-removebg-preview.png" alt="BIR Seal" />
          <div className="fd2307-agency">
            Republic of the Philippines<br />
            Department of Finance<br />
            Bureau of Internal Revenue
          </div>
        </div>
      </div>

      <div className="fd2307-form-wrap">
        <div className="fd2307-header-row">
          <div className="fd2307-formno-box">
            <div className="fd2307-formno-label">BIR Form No.</div>
            <div className="fd2307-formno-number">2307</div>
            <div className="fd2307-formno-edition">January 2018 (ENCS)</div>
          </div>

          <div className="fd2307-title-box">
            <div className="fd2307-main-title">Certificate of Creditable Tax<br />Withheld at Source</div>
          </div>

          <div className="fd2307-barcode-box">
            <img className="fd2307-barcode-img" src="/birform2307barcode-removebg-preview.png" alt="BIR Barcode" />
            <div className="fd2307-barcode-text">2307 01/18ENCS</div>
          </div>
        </div>

        <div className="fd2307-instruction">Fill in all applicable spaces. Mark all appropriate boxes with an "X".</div>

        <div className="fd2307-row-flex">
          <div className="fd2307-num-cell">1</div>
          <div className="fd2307-text-cell">
            <div className="fd2307-label-inline">For the Period</div>
            <div className="fd2307-tiny-gap" />
            <div className="fd2307-label-inline">From</div>
            <div className="fd2307-segbox fd2307-date-box">{data.startDate || ''}</div>
            <div className="fd2307-date-mm">(MM/DD/YYYY)</div>
            <div className="fd2307-label-inline fd2307-label-to">To</div>
            <div className="fd2307-segbox fd2307-date-box">{data.endDate || ''}</div>
            <div className="fd2307-date-mm">(MM/DD/YYYY)</div>
          </div>
        </div>

        <div className="fd2307-section-bar">Part I - Payee Information</div>

        <div className="fd2307-row-flex">
          <div className="fd2307-num-cell">2</div>
          <div className="fd2307-text-cell">
            <div className="fd2307-label-inline">Taxpayer Identification Number <span className="fd2307-it">(TIN)</span></div>
            <div className="fd2307-tiny-gap fd2307-gap-wide" />
            <div className="fd2307-segbox fd2307-tin-box">{data.payeeTin || ''}</div>
          </div>
        </div>

        <div className="fd2307-field-row">
          <div className="fd2307-field-top">
            <div className="fd2307-num-cell">3</div>
            <div className="fd2307-label">Payee&apos;s Name <span className="fd2307-it">(Last Name, First Name, Middle Name for Individual OR Registered Name for Non-Individual)</span></div>
          </div>
          <div className="fd2307-field-line fd2307-with-num-offset">
            <div className="fd2307-plain-box fd2307-full">{data.payeeName || ''}</div>
          </div>
        </div>

        <div className="fd2307-field-row">
          <div className="fd2307-field-top">
            <div className="fd2307-num-cell">4</div>
            <div className="fd2307-label">Registered Address</div>
            <div className="fd2307-side-note"><span className="fd2307-bold">4A</span>&nbsp;&nbsp;ZIP Code</div>
          </div>
          <div className="fd2307-field-line fd2307-with-num-offset">
            <div className="fd2307-plain-box fd2307-addr">{data.payeeAddress || ''}</div>
            <div className="fd2307-segbox fd2307-zip-box">{data.payeeZipcode || ''}</div>
          </div>
        </div>

        <div className="fd2307-field-row">
          <div className="fd2307-field-top">
            <div className="fd2307-num-cell">5</div>
            <div className="fd2307-label">Foreign Address, <span className="fd2307-it">if applicable</span></div>
          </div>
          <div className="fd2307-field-line fd2307-with-num-offset">
            <div className="fd2307-plain-box fd2307-full" />
          </div>
        </div>

        <div className="fd2307-section-bar">Part II - Payor Information</div>

        <div className="fd2307-row-flex">
          <div className="fd2307-num-cell">6</div>
          <div className="fd2307-text-cell">
            <div className="fd2307-label-inline">Taxpayer Identification Number <span className="fd2307-it">(TIN)</span></div>
            <div className="fd2307-tiny-gap fd2307-gap-wide" />
            <div className="fd2307-segbox fd2307-tin-box">{data.corpTin || ''}</div>
          </div>
        </div>

        <div className="fd2307-field-row">
          <div className="fd2307-field-top">
            <div className="fd2307-num-cell">7</div>
            <div className="fd2307-label">Payor&apos;s Name <span className="fd2307-it">(Last Name, First Name, Middle Name for Individual OR Registered Name for Non-Individual)</span></div>
          </div>
          <div className="fd2307-field-line fd2307-with-num-offset">
            <div className="fd2307-plain-box fd2307-full">{data.corpName || ''}</div>
          </div>
        </div>

        <div className="fd2307-field-row">
          <div className="fd2307-field-top">
            <div className="fd2307-num-cell">8</div>
            <div className="fd2307-label">Registered Address</div>
            <div className="fd2307-side-note"><span className="fd2307-bold">8A</span>&nbsp;&nbsp;ZIP Code</div>
          </div>
          <div className="fd2307-field-line fd2307-with-num-offset">
            <div className="fd2307-plain-box fd2307-addr">{data.corpAddress || ''}</div>
            <div className="fd2307-segbox fd2307-zip-box">{data.corpZipcode || ''}</div>
          </div>
        </div>

        <div className="fd2307-detail-block">
          <div className="fd2307-detail-title">Part III - Details of Monthly Income Payments and Taxes Withheld</div>
          <table className="fd2307-grid">
            <colgroup>
              <col className="fd2307-col-income" />
              <col className="fd2307-col-atc" />
              <col className="fd2307-col-amt" />
              <col className="fd2307-col-amt" />
              <col className="fd2307-col-amt" />
              <col className="fd2307-col-total" />
              <col className="fd2307-col-tax" />
            </colgroup>
            <tbody>
              <tr>
                <th rowSpan={2}>Income Payments Subject to Expanded Withholding Tax</th>
                <th rowSpan={2}>ATC</th>
                <th colSpan={4}>AMOUNT OF INCOME PAYMENTS</th>
                <th rowSpan={2}>Tax Withheld for the Quarter</th>
              </tr>
              <tr>
                <th>1st Month of the Quarter</th>
                <th>2nd Month of the Quarter</th>
                <th>3rd Month of the Quarter</th>
                <th>Total</th>
              </tr>

              {renderDetailRows(data.rows, data.monthOnly)}

              <tr>
                <td className="fd2307-total-label">Total</td>
                <td />
                <td />
                <td />
                <td />
                <td />
                <td className="fd2307-text-right">{formatNumber(data.totalWithholdingTax)}</td>
              </tr>

              <tr>
                <td className="fd2307-money-head">Money Payments Subject to Withholding of Business Tax (Government &amp; Private)</td>
                <td />
                <td />
                <td />
                <td />
                <td />
                <td />
              </tr>

              {Array.from({ length: 10 }).map((_, index) => (
                <tr key={`fd2307-money-empty-${index}`}>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="fd2307-declaration">
          We declare under the penalties of perjury that this certificate has been made in good faith, verified by us, and to the best of our knowledge and belief, is true and correct, pursuant to the provisions of the National Internal Revenue Code, as amended, and the regulations issued under authority thereof. Further, we give our consent to the processing of our information as contemplated under the Data Privacy Act of 2012 (R.A. No. 10173) for legitimate and lawful purposes.
        </div>

        <div className="fd2307-blank-sign">
          <div className="fd2307-sig-info">
            <div className="fd2307-sig-name">{data.repName || ''} (POSITION: {data.repPosition || ''} | TIN: {data.repTin || ''})</div>
            {data.signatureImageUrl ? (
              <img className="fd2307-sig-image" src={data.signatureImageUrl} alt="Signature" />
            ) : null}
          </div>
        </div>

        <div className="fd2307-sig-caption">
          Signature over Printed Name of Payor/Payor&apos;s Authorized Representative/Tax Agent
          <em>(Indicate Title/Designation and TIN)</em>
        </div>

        <div className="fd2307-triple-row">
          <div className="fd2307-triple-cell fd2307-w-acc"><div className="fd2307-triple-label">Tax Agent Accreditation No./<br />Attorney&apos;s Roll No. (if applicable)</div><div className="fd2307-plain-box fd2307-acct-box" /></div>
          <div className="fd2307-triple-cell fd2307-w-issue"><div className="fd2307-triple-label fd2307-small">Date of Issue <em>(MM/DD/YYYY)</em></div><div className="fd2307-segbox fd2307-issue-box" /></div>
          <div className="fd2307-triple-cell fd2307-w-exp"><div className="fd2307-triple-label fd2307-small">Date of Expiry <em>(MM/DD/YYYY)</em></div><div className="fd2307-segbox fd2307-expiry-box" /></div>
        </div>

        <div className="fd2307-conforme">CONFORME:</div>
        <div className="fd2307-blank-conforme" />

        <div className="fd2307-sig-caption">
          Signature over Printed Name of Payee/Payee&apos;s Authorized Representative/Tax Agent
          <em>(Indicate Title/Designation and TIN)</em>
        </div>

        <div className="fd2307-triple-row fd2307-no-border-bottom">
          <div className="fd2307-triple-cell fd2307-w-acc"><div className="fd2307-triple-label">Tax Agent Accreditation No./<br />Attorney&apos;s Roll No. (if applicable)</div><div className="fd2307-plain-box fd2307-acct-box" /></div>
          <div className="fd2307-triple-cell fd2307-w-issue"><div className="fd2307-triple-label fd2307-small">Date of Issue <em>(MM/DD/YYYY)</em></div><div className="fd2307-segbox fd2307-issue-box" /></div>
          <div className="fd2307-triple-cell fd2307-w-exp"><div className="fd2307-triple-label fd2307-small">Date of Expiry <em>(MM/DD/YYYY)</em></div><div className="fd2307-segbox fd2307-expiry-box" /></div>
        </div>
      </div>

      <div className="fd2307-bottom-note">*NOTE: The BIR Data Privacy is in the BIR website (www.bir.gov.ph)</div>
    </div>
  );
}
